import React, { FC, useState, useContext, useCallback, useEffect } from 'react';
import Annotations from '../../Annotations/Annotations';
import { connect } from 'react-redux';
import { RootState } from '../../../../rootReducer';
import { AppDispatch } from '../../../../store';
import { setStatusTable, saveImageSettings, getImageSettings, resetAppliedFilters } from '../annotation.slice';
import { ImageRawData } from '../annotation.types';
import { setShouldZoomToFinding } from '../../Canvas/canvas.slice';
import { TABLE_STATUS } from '../../../../shared/Helpers';
import DataQCAssessmentModal from '../Tree/QC/DataQCAssessmentModal';
import { setGoFromFindingToImage, setAnnotationIdsForDelete, setShowDeleteAnnotationDialog } from '../AnnotationForm/AnnotationForm.slice';
import useDeleteAnnotation from '../../Annotations/useDeleteAnnotation';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { ParserContext } from '../../contexts/ParserContext';
import CanvasContainer from './CanvasContainer/CanvasContainer';
import classNames from 'classnames';
import styles from '../Annotation.module.scss';
import { isStringNullOrEmpty } from '../AnnotationForm/Utils';
import { AnnotationManagerPrefix, removeFocusFromCurrentElement } from '../Helpers';
import { createImageDataQCAssessment } from '../Tree/QC/qc.slice';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = {
  image: ImageRawData | null;
  hideTree: boolean;
  hideStichedView: boolean;
  stitchedViewWidth: number;
  treeWidth: number;
} & StateProps &
  DispatchProps;

const AnnotationsOverview: FC<RouteComponentProps<any> & Props> = ({
  match,
  history,
  image,
  fullScreenMode,
  canAnnotate,
  hideTree,
  hideStichedView,
  stitchedViewWidth,
  treeWidth,
  toggleOverlayVisibility,
  turbineDataQCAssessment,
  tableStatus,
  creatingDataQCAssessment,
  havePermissionToAnnotate,
  selectedAnnotationId,
  presentAnnotations,
  selectedName,
  goFromFindingToImage,
  imageSettings,
  onShouldZoomToFinding,
  onCreateDataQCAssessment,
  onShowDeleteDialog,
  onSetAnnotationIdsForDelete,
  onAnnotationDeleted,
  onSetStatusTable,
  onSaveFilters,
  onGetImageSettings,
  resetTableFilters
}) => {
  const [showQCModal, setShowQCModal] = useState(false);

  const { parser } = useContext(ParserContext);

  useEffect(() => {
    resetTableFilters();
  }, []);

  useDeleteAnnotation(selectedAnnotationId, presentAnnotations, havePermissionToAnnotate, onSetAnnotationIdsForDelete, onShowDeleteDialog);

  useEffect(() => {
    if (goFromFindingToImage) {
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${parser.getPathUntilImage(match.params)}/${selectedName}`);
      onAnnotationDeleted(false);
    }
  }, [goFromFindingToImage, history, match.params, onAnnotationDeleted, parser, selectedName]);

  const navigateToFinding = useCallback(
    (newSequencePath: string, imageName: string, id: string) => {
      removeFocusFromCurrentElement();

      if (isStringNullOrEmpty(newSequencePath)) {
        history.push(`/${AnnotationManagerPrefix}/${parser.name}/${parser.getPathUntilImage(match.params)}/${selectedName}`);
        return;
      }
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${newSequencePath}/${imageName}/${id}`);
      onShouldZoomToFinding(true);

      if (tableStatus === TABLE_STATUS.EXPANDED) {
        onSetStatusTable(TABLE_STATUS.NORMAL);
      }
    },
    [history, match.params, onSetStatusTable, onShouldZoomToFinding, parser, selectedName, tableStatus]
  );

  const onQCSave = (qcIssuesForImage: boolean, comment: string, hasContrastIssues: boolean, isBlurred: boolean) => {
    let fullPath = '';
    const untilImagePath = parser.getPathUntilImage(match.params);
    const imageName = parser.getImageName(match.params);

    if (imageName) {
      fullPath = `${untilImagePath}/${imageName}`;
    }

    onCreateDataQCAssessment(fullPath, qcIssuesForImage, comment, isBlurred, hasContrastIssues);
    setShowQCModal(false);
  };

  const showTolbarAndActions = tableStatus !== TABLE_STATUS.EXPANDED && canAnnotate && !!image && havePermissionToAnnotate;

  return (
    <div className={classNames(styles.right, { [styles.fullScreen]: fullScreenMode || hideStichedView })}>
      <CanvasContainer
        {...{
          showTolbarAndActions,
          image,
          hideTree,
          hideStichedView,
          stitchedViewWidth,
          treeWidth,
          toggleOverlayVisibility,
          turbineDataQCAssessment,
          setShowQCModal,
          navigateToFinding,
          onSaveFilters,
          imageSettings,
          onGetImageSettings
        }}
      />
      <Annotations fullScreenMode={fullScreenMode} onRowClicked={navigateToFinding} />
      {showTolbarAndActions && (
        <DataQCAssessmentModal
          dataQCAssessment={image!.dataQCAssessment}
          showModal={showQCModal}
          onSave={onQCSave}
          closeModal={() => setShowQCModal(false)}
          isLoading={creatingDataQCAssessment}
          isTurbineQC={false}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  fullScreenMode: state.annotation.fullScreenMode,
  canAnnotate: state.annotation.canAnnotate,
  tableStatus: state.annotation.tableStatus,
  turbineDataQCAssessment: state.qc.dataQCAssessment,
  creatingDataQCAssessment: state.qc.creatingDataQCAssessment,
  havePermissionToAnnotate: state.annotation.havePermissionToAnnotate,
  selectedAnnotationId: state.annotation.selectedAnnotationId,
  presentAnnotations: state.annotation.presentAnnotations,
  toggleOverlayVisibility: state.canvas.toggleOverlayVisibility,
  selectedName: state.annotation.selectedName,
  goFromFindingToImage: state.annotationForm.goFromFindingToImage,
  imageSettings: state.annotation.imageSettings
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onShouldZoomToFinding: (shouldZoom: boolean) => dispatch(setShouldZoomToFinding(shouldZoom)),
  onCreateDataQCAssessment: (imagePath: string, haveQC: boolean, comment: string, isBlurred: boolean, hasContrastIssues: boolean) =>
    dispatch(createImageDataQCAssessment(imagePath, haveQC, comment, isBlurred, hasContrastIssues)),
  onShowDeleteDialog: () => dispatch(setShowDeleteAnnotationDialog(true)),
  onSetAnnotationIdsForDelete: (ids: string[]) => dispatch(setAnnotationIdsForDelete(ids)),
  onAnnotationDeleted: (value: boolean) => dispatch(setGoFromFindingToImage(value)),
  onSetStatusTable: (expandHide: number) => dispatch(setStatusTable(expandHide)),
  onSaveFilters: (contrast: number, brightness: number, saturation: number, applyToAll: boolean) =>
    dispatch(saveImageSettings(contrast, brightness, saturation, applyToAll)),
  onGetImageSettings: () => dispatch(getImageSettings()),
  resetTableFilters: () => dispatch(resetAppliedFilters())
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AnnotationsOverview));
